import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

const AlarmContext = createContext(null);
const STORAGE_KEY = "recho-music.alarms.v2";
const LEGACY_KEY = "recho-music.alarm";

// Day mask: 7-bit string '1111100' = Mon-Fri. Index 0 = Monday … 6 = Sunday.
function dayIdxMonStart(date) {
  // JS getDay(): 0=Sun..6=Sat. Convert to Mon-start (0=Mon..6=Sun)
  const d = date.getDay();
  return (d + 6) % 7;
}

// Compute next fire ms for an alarm
//   alarm: { time: 'HH:MM', recurring: 'once'|'daily'|'weekdays', days?: bool[7] (Mon→Sun) }
function computeNextFire(alarm) {
  if (!alarm?.time) return null;
  const [h, m] = alarm.time.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const now = new Date();

  // 'once' or 'daily': simple next occurrence
  if (alarm.recurring === "once" || alarm.recurring === "daily" || !alarm.recurring) {
    const t = new Date();
    t.setHours(h, m, 0, 0);
    if (t.getTime() <= now.getTime()) t.setDate(t.getDate() + 1);
    return t.getTime();
  }

  // 'weekdays': find next selected day
  const days = alarm.days || [false, false, false, false, false, false, false];
  if (!days.some(Boolean)) return null;
  for (let i = 0; i < 8; i++) {
    const t = new Date();
    t.setDate(t.getDate() + i);
    t.setHours(h, m, 0, 0);
    if (t.getTime() <= now.getTime()) continue;
    const idx = dayIdxMonStart(t);
    if (days[idx]) return t.getTime();
  }
  return null;
}

function mkId() {
  return `a_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function AlarmProvider({ children }) {
  const { playStation } = usePlayer();
  // Lazy initializer reads localStorage synchronously — avoids the persist-effect race.
  const [alarms, setAlarms] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      }
      // Migrate legacy single-alarm key
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) {
        const p = JSON.parse(legacy);
        if (p?.time && p?.station) {
          const migrated = [{
            id: mkId(),
            time: p.time, station: p.station,
            recurring: p.recurring === "daily" ? "daily" : "once",
            days: [false,false,false,false,false,false,false],
            enabled: !!p.enabled,
            label: "Wake",
          }];
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated)); } catch (_) { /* ignore */ }
          localStorage.removeItem(LEGACY_KEY);
          return migrated;
        }
      }
    } catch (_) { /* ignore */ }
    return [];
  });
  const timersRef = useRef({}); // id -> timeoutId

  // Persist whenever alarms change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(alarms)); } catch (_) { /* ignore quota */ }
  }, [alarms]);

  // Schedule
  useEffect(() => {
    // clear all existing timers
    Object.values(timersRef.current).forEach((t) => clearTimeout(t));
    timersRef.current = {};
    alarms.forEach((al) => {
      if (!al.enabled || !al.station || !al.time) return;
      const next = al.nextFireAt && al.nextFireAt > Date.now() ? al.nextFireAt : computeNextFire(al);
      if (!next) return;
      const delay = Math.max(0, next - Date.now());
      const capped = Math.min(delay, 2_000_000_000); // setTimeout limit
      timersRef.current[al.id] = setTimeout(() => {
        try {
          playStation(al.station);
          toast.success(`⏰ ${al.label || "Alarm"} — Now playing ${al.station.name}`, { duration: 6000 });
          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Recho Music Alarm", { body: `Now tuning in: ${al.station.name}`, icon: "/icon-192.svg" });
          }
        } catch (e) { console.error("Alarm playback failed", e); }

        // Reschedule or disable
        setAlarms((cur) => cur.map((x) => {
          if (x.id !== al.id) return x;
          if (x.recurring === "daily" || x.recurring === "weekdays") {
            return { ...x, nextFireAt: computeNextFire({ ...x, nextFireAt: null }) };
          }
          return { ...x, enabled: false, nextFireAt: null };
        }));
      }, capped);
    });
    return () => {
      Object.values(timersRef.current).forEach((t) => clearTimeout(t));
      timersRef.current = {};
    };
  }, [alarms, playStation]);

  const upsertAlarm = useCallback((alarm) => {
    const id = alarm.id || mkId();
    const next = {
      id,
      label: alarm.label || "Wake",
      time: alarm.time,
      station: alarm.station,
      recurring: alarm.recurring || "once",
      days: alarm.days || [false,false,false,false,false,false,false],
      enabled: alarm.enabled !== false,
    };
    next.nextFireAt = computeNextFire(next);
    setAlarms((cur) => {
      const idx = cur.findIndex((x) => x.id === id);
      if (idx === -1) return [next, ...cur];
      const copy = [...cur]; copy[idx] = next; return copy;
    });
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    return next;
  }, []);

  const removeAlarm = useCallback((id) => {
    setAlarms((cur) => cur.filter((x) => x.id !== id));
  }, []);

  const toggleAlarm = useCallback((id) => {
    setAlarms((cur) => cur.map((x) => x.id === id ? { ...x, enabled: !x.enabled, nextFireAt: !x.enabled ? computeNextFire(x) : null } : x));
  }, []);

  return (
    <AlarmContext.Provider value={{ alarms, upsertAlarm, removeAlarm, toggleAlarm }}>
      {children}
    </AlarmContext.Provider>
  );
}

export const useAlarm = () => useContext(AlarmContext);
